'use client';

import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
} from 'react';
import { ZERO } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useQueryClient } from '@tanstack/react-query';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import { useMarginModeSync } from './useMarginModeSync';
import AccountTradingDataSync from './AccountTradingDataSync';

const AccountTradingDataContext = createContext<boolean>(false);

export function useAccountTradingDataContext(): boolean {
	return useContext(AccountTradingDataContext);
}

/**
 * Orchestrates per-account trading data sync.
 * Separated from the provider so its frequent re-renders
 * (from useLazySubAccounts, useMarketStateStore, etc.) don't
 * cascade into the children tree.
 */
function AccountTradingDataSyncOrchestrator() {
	const accounts = useLazySubAccounts();
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const perpMarketsLookup = usePerpMarketsLookup();
	const connected = useWalletIsConnected();
	const queryClient = useQueryClient();

	const getMarketDataForMarket = useMarketStateStore(
		(s) => s.getMarketDataForMarket
	);

	const markPriceCallback = useCallback(
		(marketIndex: number) => {
			return (
				getMarketDataForMarket(MarketId.createPerpMarket(marketIndex))
					?.derivedState?.markPrice.val ?? ZERO
			);
		},
		[getMarketDataForMarket]
	);

	// Margin mode sync for current account
	useMarginModeSync();

	const shouldSync =
		connected &&
		driftClientIsReady &&
		!!driftClient &&
		perpMarketsLookup.length > 0;

	// Clear stale cache when sync stops (wallet disconnect, etc.)
	const prevShouldSync = useRef(false);
	useEffect(() => {
		if (prevShouldSync.current && !shouldSync) {
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.marginInfo(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.positions(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.orders(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.leftoverIsolatedPositions(),
			});
		}
		prevShouldSync.current = shouldSync;
	}, [shouldSync, queryClient]);

	if (!shouldSync) return null;

	return (
		<>
			{accounts.map((acct) => {
				if (!acct.client?.isSubscribed) return null;
				return (
					<AccountTradingDataSync
						key={acct.userKey}
						userKey={acct.userKey}
						user={acct.client}
						driftClient={driftClient}
						perpMarketsLookup={perpMarketsLookup}
						markPriceCallback={markPriceCallback}
					/>
				);
			})}
		</>
	);
}

/**
 * Provider that marks the account trading data system as mounted.
 * Consumer hooks use this context to warn in dev when the provider is missing.
 *
 * The sync orchestrator is a sibling of {children} so its re-renders
 * don't propagate to the rest of the app tree.
 *
 * Mounted in AppWrapper's memoizedProviders array.
 */
export default function AccountTradingDataProvider({
	children,
}: {
	children?: ReactNode;
}) {
	return (
		<AccountTradingDataContext.Provider value={true}>
			<AccountTradingDataSyncOrchestrator />
			{children}
		</AccountTradingDataContext.Provider>
	);
}
